import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { ThemeProvider } from 'emotion-theming'
import { connect } from 'react-redux';
import _get from 'lodash.get';
import _isEqual from 'lodash.isequal';
import Links from 'components/Link';
import "./PrintArticle.scss";
import Text from 'routes/articleshow_v2/components/common/Text';
import { themedefault } from '../../../../components/Print/Theme';
import { StyledContainer } from '../../../../components/Print/Container';
import { device } from '../../../../components/Print/device';
import { Synopsis, SynopsisWrapper } from '../../../../components/Print/Synopsis';
import { getCustomClassesForPayload, getSkipAdsFlagForVideo } from 'utils/common';
import CData from 'components_v2/common/CData';
import Quotes from 'routes/articleshow_v2/components/common/Quotes';
import VideoWidget from 'routes/articleshow_v2/components/common/VideoWidget/VideoWidget';
import Slideshow from 'routes/articleshow_v2/components/common/Slideshow';
import Timeline from 'routes/articleshow/components/Timeline';
import Pdf from 'components/Pdf/Pdf';
import Ppt from 'components/Ppt/Ppt';
import ArticleTopSection from './ArticleTopSection';
import Image from 'routes/articleshow_v2/components/common/Image';
import ReadAlsoWidget from 'routes/articleshow_v2/components/common/ReadAlsoWidget/ReadAlsoWidget';
import PdfEmbed from 'routes/articleshow_v2/components/common/PdfEmbed/PdfEmbedLoader';
import SocialWidget from 'routes/articleshow_v2/components/desktop/SocialSharingWeb';
import WithCommentLoader from 'modules/WithComments/WithCommentLoader';
import SocialSharingMobile from 'routes/articleshow_v2/components/mobile/SocialSharingMobile/SocialSharingMobile';
import TopComment from 'routes/articleshow_v2/components/common/TopComment/TopComment';
import { openSidebar } from 'reduxUtils/modules/comments';
import ToiPrintFaq from 'components/Print/ToiPrintFaq';
import ToipNudges from 'components_v2/common/ToipNudges';
import StoryBlockerPopUp from '../../../../components_v2/common/ToipNudges/StoryBlockerV3/StoryBlockerPopUp';

interface IUserData {
  isLoggedIn?: boolean;
  prcValue?: number;
  showConsentPopUp?: boolean;
}

interface IPrintAsProps {
  articleShowData: { [key: string]: any };
  msid: string;
  isWapView: boolean;
  userData: IUserData | undefined
  comments: any
  urlPath: string
  showSidebar: (params: string, id: string) => void;
  isUserPrime: boolean
  storyTitle: string
  isAppView: boolean
  categoryForGA: string
  isUpgrade: boolean
  isEpaperApp: boolean
}

const PrimePrintEditionAs = (props: IPrintAsProps) => {
  const { articleShowData, msid, isWapView, comments, isUserPrime, storyTitle, showSidebar, isAppView, categoryForGA, userData, isUpgrade, isEpaperApp } = props;
  const [textSize, setTextSize] = useState<string>('medium');
  const [showPopUp, setShowPopUp] = useState<boolean>(false);
  const isLoggedIn =
    userData && userData.isLoggedIn ? userData.isLoggedIn : false;
  const commentTextInput = useRef();

  const scrollToBlocker = () => {
    const ele =
      typeof window !== 'undefined' && document.getElementById('print-blocker-nudge');
    if (
      ele && typeof ele.scrollIntoView === 'function'
    ) {
      ele.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const shouldShowPopUp = () => {
    setShowPopUp(true);
  };

  if (!articleShowData) {
    return null;
  }
  const getComponents = (data: { [key: string]: any }, isWapView: boolean, key: number, showIframe: boolean) => {
    let component;
    if (data.tn) {
      switch (data.tn) {
        case 'text':
          component = (
            <Text
              value={data.value}
              tags={data.tags}
              printLayout
            />
          );
          break;
        case 'link':
          {/* @ts-ignore */ }
          component = <Links href={data.wu}>{data.value}</Links>;
          break;
        case 'keyword':
          {/* @ts-ignore */ }
          component = isUserPrime ? <Links
            href={data.wu}
            className={data.tags || ''}
            data-ga={`within_article-topic_link|topic_${data.wu.replace(
              'https://timesofindia.indiatimes.com/topic/',
              '',
            )}`}
          >
            {data.value}
          </Links> : data.value;
          break;
        case 'br':
          component = <BreakElement />;
          break;
        case 'inlineimage':
          component = (
            <Image
              title={data.title}
              caption={data.cap}
              // url={data.wu}
              target={data.target}
              altTitle={data.title}
              msid={data.imageId}
              imagesize={data.imagesize}
              overridelink={data.wu}
              styleObj={{}}
              key={key}
              skipAffiliateHandling={articleShowData.ag === 'Mediawire'}
            />
          );
          break;
        case 'cdata': {
          component = (
            <CData
              styleObj={{}}
              isJumpLinkParent={!!data.cmsannotationId}
              value={data.value}
              type={data.type}
              pos={key}
            />
          );
          break;
        }
        case 'quote':
          component = <Quotes  {...data} prime styleObj={{}} />;
          break;
        case 'extquote':
          component = <Quotes  {...data} prime styleObj={{}} />;
          break;
        case 'inlinevideo': {
          const isVideoSkipAds = getSkipAdsFlagForVideo(
            articleShowData.metaInfo,
          );
          component = (
            <VideoWidget
              video={data}
              isVideoSkipAds={isVideoSkipAds}
              styleObj={{}}
              playerContainerId={`Articleshow_${data.id}_InlineVideo${key}`}
              iconClassName='as_playicon'
              imageClassName='inline_photo content'
              videoClassName='content'
              isWapView={isWapView}
              isUserPrime={isUserPrime}
              notitle
              enableNextVideos
              dataFirstPlayClickGa={`video-inline-user_initiated|msid_${articleShowData.id
                }-video_${data && data.id ? data.id : ''}`}
              dataCloseClickGa={`video-inline-close_video|msid_${articleShowData.id
                }-video_${data && data.id ? data.id : ''}`}
            />
          );
          break;
        }
        case 'slideshow': {
          component = <Slideshow
            {...data}
            isWapView={isWapView}
            wu={articleShowData.wu}
          />
          break;
        }
        case 'timeline':
          component = (
            <Timeline
              {...data}
              isWapView={isWapView}
              getComponents={getComponents}
            />
          );
          break;
        case 'pdf':
          component = (
            <Pdf
              {...data}
              isWapView={isWapView}
              classes={
                data.class
                  ? getCustomClassesForPayload(data.class.toLowerCase())
                  : ''
              }
            />
          );
          break;
        case 'document':
          if (data && data.type === 'pdf' && data.wu) {
            component = (
              <PdfEmbed
                feedPdfCountUrl={data.wu}
                imageUrl={data.imageUrl}
                msid={data.id ? data.id : data.msid}
                title={data.value}
                isWapView={isWapView}

              />
            );
          }
          break;
        case 'ppt':
          component = (
            <Ppt
              {...data}
              isWapView={isWapView}
              classes={
                data.class
                  ? getCustomClassesForPayload(data.class.toLowerCase())
                  : ''
              }
            />
          );
          break;
        case 'readalso':
          if (data.contents instanceof Array && data.contents.length) {
            component = data.contents.map((item: { [key: string]: any }) => (
              <ReadAlsoWidget data={item} styleObj={{}} />
            ));
          }
          break;
        default:
          component = '';
      }
    }
    return component;
  }

  const onSizeChange = (currSize: string) => {
    const _classes = ['small', 'medium', 'large'];
    const ele: any = document.querySelector('html');
    if (!ele.classList.contains(currSize)) {
      // remove all classes first and add newone
      _classes.map(x => ele.classList.remove(x));
      ele.classList.add(currSize);
    }
    setTextSize(currSize)
  };

  const getSectionName = () => {
    const sectionName =
      articleShowData.navsubsecs &&
        articleShowData.navsubsecs.subsec1 &&
        articleShowData.navsubsecs.subsec1.subsecname1
        ? articleShowData.navsubsecs.subsec1.subsecname1
        : '';

    return sectionName.toLowerCase();
  }

  const getCommentModerationData = (): string => {
    return _get(articleShowData, 'metaInfo.CommentModeration', '');
  }

  const getCommentsCount = (msid: String): number => {
    let commentCount = 0;
    if (comments && comments[`commentsData-${msid}`]) {
      const commentsData = comments[`commentsData-${msid}`];
      commentCount = commentsData[0] ? commentsData[0].totalcount : 0;
    }
    return commentCount;
  };

  const getComments = (msid: string) => {
    return comments && comments[`commentsData-${msid}-agreed`] ? comments[`commentsData-${msid}-agreed`] : [];
  };

  const openSideBar = (msid: string, id: string) => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = '';
    showSidebar(msid, id);
  };
  const commentsData = getComments(msid);
  const commentsCount = getCommentsCount(msid);
  const commentModeration = getCommentModerationData();
  const sectionName = getSectionName();
  return (
    <ThemeProvider theme={themedefault}>
      <StyledContainer usertype={'nonprime'}>
        <ArticleTopSection articleShowData={articleShowData} isWapView={isWapView} scrollToBlocker={scrollToBlocker} isUserPrime={isUserPrime} comments={comments} msid={msid} textSize={textSize} changeSize={onSizeChange} />
        {(isEpaperApp || isUserPrime) && Array.isArray(articleShowData.fullStory) && articleShowData.fullStory.length ? <SynopsisWrapper>
          <Synopsis dataFont={textSize} type={''}>
            {articleShowData.fullStory.map((el: { [key: string]: any }, key: number) => getComponents(el, isWapView, key, true))}
          </Synopsis>
        </SynopsisWrapper> : null}
        {!isEpaperApp && !isUserPrime && Array.isArray(articleShowData.story) && articleShowData.story.length ? <SynopsisWrapper>
          <Synopsis dataFont={''} type={'synopsis'}>
            {articleShowData.story.map((el: { [key: string]: any }, key: number) => getComponents(el, isWapView, key, true))}
          </Synopsis>
        </SynopsisWrapper> : null}
      </StyledContainer>
      {!isUserPrime ?
        <>
          <BlockerWrapper id="blocker">
            <ToipNudges viewType="printArticleShow" isWapView={isWapView} msid={msid} isAppView={isAppView} userData={userData} storyTitle={storyTitle} isUpgrade={isUpgrade} shouldShowPopUp={shouldShowPopUp}/>
          </BlockerWrapper>
          <StyledContainer id="fq">
            <ToiPrintFaq usertype='nonprime' />
          </StyledContainer>
          {showPopUp && (
            <StoryBlockerPopUp isWapView={isWapView} msid={msid}>
              <ToipNudges
                viewType="printHomePage"
                isWapView={isWapView}
                isAppView={isAppView}
                userData={userData}
                isUpgrade={isUpgrade}
                storyTitle={storyTitle}
                msid={msid}
                shouldShowPopUp={shouldShowPopUp}
                blockerPopUp
              />
            </StoryBlockerPopUp>
          )}

        </> :
        <StyledContainer usertype={'nonprime'}>
          {!isEpaperApp && isLoggedIn && <TopComment
            msid={articleShowData.id}
            href={articleShowData.seo.canonical}
            onSidebarOpenClose={openSideBar}
            commentsData={commentsData}
            commentsCount={commentsCount}
            isWapView={isWapView}
            isPrivateUser={false}
            commentModeration={commentModeration}
            commentTextInput={commentTextInput}
            gaCategory={categoryForGA}
            gaAction="click"
            gaLabel="Comments_click"
            printLayout
          />}
          {!isEpaperApp && isLoggedIn && <div id={`comment-sidebar-${msid}`}>
            <WithCommentLoader
              msid={msid}
              isWapView={isWapView}
              commentTextInputRef={commentTextInput}
              seoData={articleShowData.seo}
              commentModeration={commentModeration}
              hideBottomComments={true}
              printLayout
              articleTitle={articleShowData.hl}
              articleSynopsys={articleShowData.des}
              showCommentsOnMobile={isWapView}
            />
          </div>}
          <SocialWidgetWrapper>
            <SocialTitle>FOLLOW US ON SOCIAL MEDIA </SocialTitle>
            {isWapView ? (
              <SocialSharingMobile
                textSize={textSize}
                isWapView
                hideSharingButtons
                showFollowButtons
                // textRequired
                wrapperClass="mobile-socialshare-bottom"
                gaCategory={categoryForGA}
                gaAction='click'
                gaIndex='btfFollow_'
                sectionName={sectionName}
                printLayout
              />) :
              <SocialWidget
                hideSharingButtons
                showFollowButtons
                isWapView={isWapView}
                type="BTF"
                gaCategory={categoryForGA}
                gaAction="click"
                gaIndex="btfFollow_"
                textRequired
                sectionName={sectionName}
                printLayout
              />}
          </SocialWidgetWrapper>
        </StyledContainer>}
    </ThemeProvider >
  );
}

const mapDispatchToProps = (dispatch: any) => ({
  showSidebar: (params: string, id: string) => {
    dispatch(openSidebar(params, id));
  },
});

const mapStateToProps = (state: any) => ({
  comments: state.comments,
});

export default connect(mapStateToProps, mapDispatchToProps)(PrimePrintEditionAs);
export const SocialWidgetWrapper = styled.section`
  margin:24px 0 0 0;
  @media ${device.mobile}{
    padding: 16px 0;
    margin:0;
  }
  width: 100%;
  text-align: center;
`
export const SocialTitle = styled.h4`
  font-size: 14px;
  font-weight: 400;
  padding: 0 0 12px;
`
const BlockerWrapper = styled.section`
max-width: 1280px;
width: 100%;
margin: 0 auto;
padding: 0 16px;
@media screen and (min-width: 768px) and (max-width: 1240px) { 
  padding: 0 16px;
}
  @media ${device.mobile}{
    padding: 0;
    width: 100%;
  }
`
const BreakElement = styled.span`
  content: '.';
  display: block;
  text-indent: -100000px;
  color: var(--white-color);
  height: 30px;

  @media ${device.mobile}{
    height: 17px;
  }
`
